import * as Sentry from '@sentry/react';
import axios from 'axios';
import { getConfig } from '@app/services/configService';
import { OPAQUE_ERROR_CODES, type NormalizedApiError } from '@app/api/errorNormalizer';
import type { ICanton } from '@app/interfaces/config';

class ApiRequestError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

interface ApiRequestInfo {
  method?: string;
  url?: string;
}

let enabled = false;

// console levels kept as breadcrumbs.
const KEPT_CONSOLE_LEVELS = new Set(['warning', 'error', 'fatal']);

export const initSentry = (): void => {
  const { sentryEnabled, sentryDsn, sentryEnvironment, sentryReplayOnErrorSampleRate, version } = getConfig();

  if (!sentryEnabled) {
    console.info('[sentry] disabled (sentryEnabled is not true in config.json)');
    return;
  }
  if (!sentryDsn) {
    console.info('[sentry] enabled but no sentryDsn in config.json — nothing will be reported');
    return;
  }

  Sentry.init({
    dsn: sentryDsn,
    environment: sentryEnvironment ?? import.meta.env.MODE,
    release: version,
    sendDefaultPii: false,
    integrations: [
      Sentry.replayIntegration({ maskAllText: true, maskAllInputs: true, blockAllMedia: true }),
      // `registerGlobalErrorHandlers` captures rejections itself to get a reference id for the toast; letting Sentry capture them too would duplicate every event.
      Sentry.globalHandlersIntegration({ onerror: true, onunhandledrejection: false }),
    ],
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: sentryReplayOnErrorSampleRate ?? 0.1,
    tracesSampleRate: 0,
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === 'console' && !KEPT_CONSOLE_LEVELS.has(breadcrumb.level ?? '')) {
        return null;
      }
      return breadcrumb;
    },
    beforeSend(event, hint) {
      // The axios interceptor reports failed requests via `captureApiError`; an unhandled rejection of the same AxiosError would only duplicate it.
      if (axios.isAxiosError(hint.originalException)) {
        return null;
      }
      return event;
    },
  });

  enabled = true;
};

/** Numeric and UUID path segments become `:id` so one endpoint groups into one issue. */
const toRouteTemplate = (url: string): string => {
  const path = url.split('?')[0];
  return path
    .split('/')
    .map((segment) =>
      /^\d+$/.test(segment) || /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)
        ? ':id'
        : segment,
    )
    .join('/');
};

const isReportableApiError = (normalized: NormalizedApiError): boolean =>
  !normalized.isCanceled &&
  ((normalized.isNetworkError && navigator.onLine !== false) ||
    normalized.isTimeout ||
    (normalized.status != null && normalized.status >= 500) ||
    (normalized.errorCode != null && OPAQUE_ERROR_CODES.has(normalized.errorCode)));

const API_REPORT_THROTTLE_MS = 60_000;
const recentApiReports = new Map<string, { eventId: string; sentAt: number }>();

/**
 * Sends server crashes, timeouts and network failures. 4xx responses are expected
 * business outcomes (validation, permissions) and are not sent. A repeat inside
 * {@link API_REPORT_THROTTLE_MS} returns the earlier event's id, so the reference
 * a user quotes always exists in Sentry.
 * @returns the Sentry event id, or `undefined` when nothing was sent.
 */
export const captureApiError = (normalized: NormalizedApiError, request?: ApiRequestInfo): string | undefined => {
  if (!enabled || !isReportableApiError(normalized)) {
    return undefined;
  }

  const method = request?.method?.toUpperCase() ?? 'UNKNOWN';
  const route = toRouteTemplate(request?.url ?? '');
  const statusLabel =
    normalized.status != null ? String(normalized.status) : normalized.isTimeout ? 'timeout' : 'network';

  const throttleKey = `${method} ${route} ${statusLabel}`;
  const now = Date.now();
  const recent = recentApiReports.get(throttleKey);
  if (recent && now - recent.sentAt < API_REPORT_THROTTLE_MS) {
    return recent.eventId;
  }
  recentApiReports.forEach((report, key) => {
    if (now - report.sentAt >= API_REPORT_THROTTLE_MS) {
      recentApiReports.delete(key);
    }
  });

  const eventId = Sentry.captureException(new ApiRequestError(`${method} ${route} failed (${statusLabel})`), {
    tags: {
      'http.method': method,
      'http.status': statusLabel,
      error_code: normalized.errorCode ?? 'none',
    },
    extra: {
      url: request?.url,
      errorMessage: normalized.errorMessage,
      errorDetails: normalized.errorDetails,
    },
    fingerprint: [method, route, statusLabel],
  });
  recentApiReports.set(throttleKey, { eventId, sentAt: now });
  return eventId;
};

/**
 * Promise rejections nothing caught, reported from `registerGlobalErrorHandlers`.
 * @returns the Sentry event id, or `undefined` when Sentry is disabled.
 */
export const captureUnhandledError = (reason: unknown): string | undefined => {
  if (!enabled) {
    return undefined;
  }
  return Sentry.captureException(reason, { tags: { source: 'unhandledrejection' } });
};

/** @returns the Sentry event id, or `undefined` when Sentry is disabled. */
export const captureRenderError = (error: Error, componentStack?: string | null): string | undefined => {
  if (!enabled) {
    return undefined;
  }
  return Sentry.captureException(error, {
    contexts: { react: { componentStack: componentStack ?? '' } },
  });
};

interface SentryUser {
  id?: number | string;
}

/** Called with no argument on logout, which clears the user from later events. */
export const setSentryUser = (user?: SentryUser): void => {
  if (!enabled) {
    return;
  }
  if (user?.id == null) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({ id: String(user.id) });
};

export const setSentryCanton = (canton?: ICanton): void => {
  if (!enabled) {
    return;
  }
  Sentry.setTag('canton', canton?.name);
  Sentry.setTag('canton_id', canton?.accountId);
};
